/**
 * EntityMap
 *
 * Renders a Leaflet map for a WKT geometry literal.
 * Supports EWKT (SRID=xxxx;...) and OGC CRS prefix formats.
 * Uses proj4 for coordinate transformations when the CRS is not WGS84.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import proj4 from 'proj4';
import { wktToGeoJSON } from 'betterknown';
import styles from './EntityPage.module.scss';

// Fix Leaflet's default marker icon paths broken by bundlers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Common CRS proj4 definitions
const PROJECTIONS: Record<number, string> = {
  4326: '+proj=longlat +datum=WGS84 +no_defs',
  3857: '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +no_defs',
  4258: '+proj=longlat +ellps=GRS80 +no_defs',
  3035: '+proj=laea +lat_0=52 +lon_0=10 +x_0=4321000 +y_0=3210000 +ellps=GRS80 +units=m +no_defs',
  25832: '+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs',
  25833: '+proj=utm +zone=33 +ellps=GRS80 +units=m +no_defs',
  31370: '+proj=lcc +lat_1=51.16667 +lat_2=49.83333 +lat_0=90 +lon_0=4.367 +x_0=150000 +y_0=5400088 +ellps=intl +units=m +no_defs',
};

// GeoJSON coordinate recursion
type Coords = number[] | number[][] | number[][][] | number[][][][];

function transformCoords(coords: Coords, transform: proj4.Converter): Coords {
  if (typeof (coords as number[])[0] === 'number') {
    const [x, y, ...rest] = coords as number[];
    const [tx, ty] = transform.forward([x, y]);
    return [tx, ty, ...rest];
  }
  return (coords as Coords[]).map((c) => transformCoords(c as Coords, transform)) as Coords;
}

// Swap [lat, lon] → [lon, lat] for CRSs that use latitude-first axis order (e.g. EPSG:4326)
function swapAxisCoords(coords: Coords): Coords {
  if (typeof (coords as number[])[0] === 'number') {
    const [x, y, ...rest] = coords as number[];
    return [y, x, ...rest];
  }
  return (coords as Coords[]).map((c) => swapAxisCoords(c as Coords)) as Coords;
}

// Recursively apply a coordinate mapper to any geometry (including GeometryCollection)
function mapGeometryCoords(
  geom: GeoJSON.Geometry,
  mapper: (coords: Coords) => Coords,
): GeoJSON.Geometry {
  if (geom.type === 'GeometryCollection') {
    return {
      type: 'GeometryCollection',
      geometries: geom.geometries.map((g) => mapGeometryCoords(g, mapper)),
    };
  }
  // All other geometry types have a `coordinates` property
  const g = geom as Exclude<GeoJSON.Geometry, GeoJSON.GeometryCollection>;
  return { ...g, coordinates: mapper(g.coordinates as Coords) } as GeoJSON.Geometry;
}

interface ParsedGeometry {
  geojson: GeoJSON.Geometry | null;
  srid: number | null;
  /** True when the WKT uses latitude-first axis order (e.g. EPSG:4326) and coords must be swapped for GeoJSON */
  swapAxisOrder: boolean;
}

function parseWkt(wktLiteral: string): ParsedGeometry {
  let wkt = wktLiteral.trim();
  let srid: number | null = null;
  let swapAxisOrder = false;

  // EWKT: SRID=4326;POINT(...)
  const ewktMatch = wkt.match(/^SRID=(\d+);(.+)$/i);
  if (ewktMatch) {
    srid = parseInt(ewktMatch[1], 10);
    wkt = ewktMatch[2];
    // EPSG:4326 defines axis order as latitude, longitude
    if (srid === 4326) swapAxisOrder = true;
  }

  // OGC CRS84 (lon/lat — same as GeoJSON default, no swap needed)
  // <http://www.opengis.net/def/crs/OGC/1.3/CRS84> POINT(...)
  const ogcCrs84Match = wkt.match(/^<http:\/\/www\.opengis\.net\/def\/crs\/OGC\/1\.3\/CRS84>\s+(.+)$/i);
  if (ogcCrs84Match) {
    wkt = ogcCrs84Match[1];
    swapAxisOrder = false;
  }

  // OGC EPSG: <http://www.opengis.net/def/crs/EPSG/0/XXXX> POINT(...)
  const ogcEpsgMatch = wkt.match(/^<http:\/\/www\.opengis\.net\/def\/crs\/EPSG\/0\/(\d+)>\s+(.+)$/i);
  if (ogcEpsgMatch) {
    srid = parseInt(ogcEpsgMatch[1], 10);
    wkt = ogcEpsgMatch[2];
    // EPSG:4326 uses lat/lon axis order
    if (srid === 4326) swapAxisOrder = true;
  }

  // betterknown only skips plain spaces, so collapse newlines/tabs/runs of whitespace
  wkt = wkt.replace(/\s+/g, ' ');

  let geojson: ReturnType<typeof wktToGeoJSON>;
  try {
    geojson = wktToGeoJSON(wkt);
  } catch {
    // Retry with dimension tags inferred from the coordinates, for literals where
    // a tag is missing or inconsistent (e.g. LINESTRING(x y z) inside a collection)
    try {
      geojson = wktToGeoJSON(inferDimensionTags(wkt));
    } catch (err) {
      console.warn('[EntityMap] Failed to parse WKT geometry:', err);
      return { geojson: null, srid, swapAxisOrder };
    }
  }
  return { geojson: geojson ?? null, srid, swapAxisOrder };
}

// Rewrite each geometry's Z/M/ZM tag to match the number of values in its first coordinate
function inferDimensionTags(wkt: string): string {
  return wkt.replace(
    /\b(POINT|LINESTRING|POLYGON|MULTIPOINT|MULTILINESTRING|MULTIPOLYGON)\s*(?:ZM|Z|M)?\s*(\(+)\s*([^,()]+)/gi,
    (_match, type: string, parens: string, firstCoord: string) => {
      const dims = firstCoord.trim().split(' ').length;
      const tag = dims >= 4 ? ' ZM' : dims === 3 ? ' Z' : '';
      return `${type}${tag}${parens}${firstCoord}`;
    },
  );
}

interface EntityMapProps {
  wkt: string;
}

const EntityMap = ({ wkt }: EntityMapProps): JSX.Element => {
  const { t } = useTranslation();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const { geojson, srid, swapAxisOrder } = parseWkt(wkt);

    if (!geojson) {
      console.warn('[EntityMap] Failed to parse WKT:', wkt);
      return;
    }

    let transformedGeojson: GeoJSON.Geometry = geojson;

    // EPSG:4326 encodes coordinates as (lat, lon) — swap to GeoJSON (lon, lat)
    if (swapAxisOrder) {
      transformedGeojson = mapGeometryCoords(transformedGeojson, swapAxisCoords);
    }

    // Apply coordinate transformation if needed
    if (srid && srid !== 4326 && PROJECTIONS[srid]) {
      proj4.defs(`EPSG:${srid}`, PROJECTIONS[srid]);
      proj4.defs('EPSG:4326', PROJECTIONS[4326]);
      const transformFn = proj4(`EPSG:${srid}`, 'EPSG:4326');
      transformedGeojson = mapGeometryCoords(transformedGeojson, (c) =>
        transformCoords(c, transformFn),
      );
    }

    const map = L.map(mapContainerRef.current);
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    const featureData: GeoJSON.Feature = { type: 'Feature', properties: {}, geometry: transformedGeojson };

    const geoLayer = L.geoJSON(featureData as GeoJSON.GeoJsonObject, {
      style: {
        color: '#0057a8',
        weight: 3,
        opacity: 0.8,
        fillColor: '#0057a8',
        fillOpacity: 0.2,
      },
      pointToLayer: (_feature, latlng) =>
        L.circleMarker(latlng, {
          radius: 8,
          fillColor: '#0057a8',
          color: '#ffffff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.9,
        }),
      onEachFeature: (feature, layer) => {
        const geom = feature.geometry;
        if (!geom) return;
        if (geom.type === 'Point') {
          const [lon, lat] = geom.coordinates as number[];
          layer.bindPopup(
            `<strong>Point</strong><br>Lon: ${lon.toFixed(6)}<br>Lat: ${lat.toFixed(6)}`
          );
        } else if (geom.type === 'LineString') {
          layer.bindPopup(
            `<strong>LineString</strong><br>${(geom.coordinates as number[][]).length} points`
          );
        } else if (geom.type === 'Polygon') {
          layer.bindPopup(
            `<strong>Polygon</strong><br>${(geom.coordinates as number[][][])[0].length} vertices`
          );
        }
      },
    }).addTo(map);

    const bounds = geoLayer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    } else {
      map.setView([60.0, 10.75], 6); // Default: Norway
    }

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [wkt]);

  return (
    <div className={styles.mapWrapper}>
      <div ref={mapContainerRef} className={styles.mapContainer} />
      <details className={styles.wktDetails}>
        <summary className={styles.wktSummary}>{t('entity.wktLiteral')}</summary>
        <pre className={styles.wktCode}><code>{wkt}</code></pre>
      </details>
    </div>
  );
};

export default EntityMap;
